import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';

interface Options {
  /** Fires the server-side refresh. Should enqueue and return quickly (202). */
  trigger: () => Promise<unknown>;
  /** Query keys to re-fetch as the background work lands. */
  queryKeys: QueryKey[];
  /** How long to keep polling for the new data (ms). */
  durationMs?: number;
  /** How often to re-fetch while polling (ms). */
  intervalMs?: number;
}

/**
 * Make a "Refresh" button feel instant: fire a server refresh that enqueues the
 * slow work and returns immediately, then poll the given queries a few times so
 * the freshly-computed data shows up without the button hanging on the request.
 *
 * `isRefreshing` stays true for the polling window so the UI can show a subtle
 * "updating…" state, but the click itself resolves right away.
 */
export function useBackgroundRefresh({
  trigger,
  queryKeys,
  durationMs = 20_000,
  intervalMs = 3_000,
}: Options) {
  const qc = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };
  useEffect(() => clearTimers, []);

  const start = useCallback(async () => {
    setError(null);
    setIsRefreshing(true);
    try {
      await trigger();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setIsRefreshing(false);
      return;
    }
    clearTimers();
    const invalidate = () =>
      queryKeys.forEach((k) => qc.invalidateQueries({ queryKey: k }));
    for (let t = intervalMs; t <= durationMs; t += intervalMs) {
      timers.current.push(setTimeout(invalidate, t));
    }
    timers.current.push(setTimeout(() => setIsRefreshing(false), durationMs));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, qc, durationMs, intervalMs, JSON.stringify(queryKeys)]);

  // `error` carries the trigger's failure (e.g. the API's validation message).
  return { isRefreshing, isError: error !== null, error, start };
}
